/**
 * Active Run Pill
 * Global mini-controller that appears when a run is active
 * Visible on all pages - ONLY when user is authenticated
 */

import { useRunSession } from '@/store/run-session';
import { useNavigate, useLocation } from 'react-router-dom';
import { Play, Pause, Square, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trackingService } from '@/services/tracking-service';
import { useNativeHaptics } from '@/hooks/useNativeHaptics';
import { logger } from '@/lib/logger';
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

export function ActiveRunPill() {
  const { user } = useAuth();
  const { status, duration, distance, pauseRun, resumeRun, finishRun, sessionId, setProcessedRunId } = useRunSession();
  const navigate = useNavigate();
  const location = useLocation();
  const { success: hapticSuccess } = useNativeHaptics();
  const { toast } = useToast();
  // Initialize with validated duration
  const [localDuration, setLocalDuration] = useState(() => {
    return duration > 0 && duration < 86400 ? duration : 0;
  });
  const [isProcessing, setIsProcessing] = useState(false);

  // Don't show on run page, if not active, OR if user is not authenticated
  const shouldShow = user && 
                      (status === 'running' || status === 'paused') && 
                      !location.pathname.startsWith('/run');

  // Update duration every second when running
  useEffect(() => {
    // ✅ ALWAYS validate duration, regardless of status
    const safeDuration = duration > 0 && duration < 86400 ? duration : 0;
    setLocalDuration(safeDuration);

    // Only run interval if running
    if (status !== 'running') return;
    
    const interval = setInterval(() => {
      setLocalDuration(prev => {
        const next = prev + 1;
        // Safety: prevent runaway timer
        return next < 86400 ? next : prev;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [status, duration]);

  const handlePause = async () => {
    try {
      hapticSuccess();
      await trackingService.pauseTracking();
      pauseRun();
      logger.info('Run paused from pill');
    } catch (error) {
      logger.error('Error pausing run from pill', error);
    }
  };

  const handleResume = async () => {
    try {
      hapticSuccess();
      await trackingService.resumeTracking();
      resumeRun();
      logger.info('Run resumed from pill');
    } catch (error) {
      logger.error('Error resuming run from pill', error);
    }
  };

  const handleFinishRun = async () => {
    if (isProcessing) {
      logger.warn('Already processing finish, ignoring duplicate call');
      return;
    }

    try {
      setIsProcessing(true);
      hapticSuccess();
      logger.info('[ActiveRunPill] Starting finish run process', { 
        sessionId, 
        status,
        distance,
        duration 
      });

      // If running, pause first
      if (status === 'running') {
        await trackingService.pauseTracking();
        pauseRun();
        await new Promise(resolve => setTimeout(resolve, 500)); // Give time for GPS to settle
      }

      // Stop tracking and collect data
      logger.info('[ActiveRunPill] Stopping tracking service');
      const finalData = await trackingService.stopTracking();
      
      if (!finalData || finalData.points.length === 0) {
        logger.error('[ActiveRunPill] No GPS data collected');
        toast({
          variant: 'destructive',
          title: 'Erro ao finalizar',
          description: 'Nenhum dado GPS foi coletado. Tente novamente.',
        });
        setIsProcessing(false);
        return;
      }

      // Use values from global store (already calculated during run)
      const totalDistance = distance; // in meters
      const elapsedTime = duration; // in seconds

      logger.info('[ActiveRunPill] GPS data collected', { 
        points: finalData.points.length,
        distance: totalDistance,
        duration: elapsedTime 
      });

      // Validate minimum GPS points
      if (finalData.points.length < 3) {
        logger.error('[ActiveRunPill] Insufficient GPS data', { 
          points: finalData.points.length 
        });
        toast({
          variant: 'destructive',
          title: 'GPS insuficiente',
          description: `Apenas ${finalData.points.length} pontos coletados. Continue correndo por mais tempo.`,
        });
        setIsProcessing(false);
        return;
      }

      // Mark as finished before processing
      sessionStorage.setItem('run_finished', 'true');
      sessionStorage.removeItem('active_run_state');

      logger.info('[ActiveRunPill] Processing run with edge function');
      const { data: processedRun, error: processError } = await supabase.functions.invoke('process-run', {
        body: {
          sessionId,
          gpsPoints: finalData.points,
          totalDistance,
          elapsedTime,
          metrics: finalData.metrics,
        }
      });

      logger.info('[ActiveRunPill] Process run response', { 
        processedRun,
        hasRunId: !!processedRun?.run_id,
        rawResponse: JSON.stringify(processedRun)
      });

      if (processError) {
        logger.error('[ActiveRunPill] Error processing run', processError);
        // Save offline for later sync
        const offlineRun = {
          sessionId,
          gpsPoints: finalData.points,
          totalDistance,
          elapsedTime,
          timestamp: Date.now(),
        };
        sessionStorage.setItem('offline_run', JSON.stringify(offlineRun));
        
        finishRun();
        navigate('/run/summary/offline');
        setIsProcessing(false);
        return;
      }

      const runId = processedRun?.run_id;
      logger.info('[ActiveRunPill] Run processed successfully', { runId });

      // Update global store
      finishRun();
      setProcessedRunId(runId);

      // Navigate to summary
      navigate(`/run/summary/${runId}`);
      setIsProcessing(false);

    } catch (error) {
      logger.error('[ActiveRunPill] Error finishing run', error);
      toast({
        variant: 'destructive',
        title: 'Erro ao finalizar',
        description: 'Ocorreu um erro ao finalizar a corrida.',
      });
      setIsProcessing(false);
    }
  };

  const handleGoToRun = () => {
    hapticSuccess();
    navigate('/run');
  };

  const formatTime = (seconds: number) => {
    // Sanity check: ensure seconds is within valid range (0 to 24h)
    const safeSeconds = seconds > 0 && seconds < 86400 ? seconds : 0;
    
    const h = Math.floor(safeSeconds / 3600);
    const m = Math.floor((safeSeconds % 3600) / 60);
    const s = safeSeconds % 60;
    return h > 0 
      ? `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
      : `${m}:${s.toString().padStart(2, '0')}`;
  };

  const formatDistance = (meters: number) => {
    return meters >= 1000 
      ? `${(meters / 1000).toFixed(2)} km`
      : `${meters.toFixed(0)} m`;
  };

  if (!shouldShow) return null;

  return (
    <div 
      className="fixed left-1/2 -translate-x-1/2 bottom-[calc(env(safe-area-inset-bottom)+80px)] z-50 
                 bg-primary text-primary-foreground rounded-full shadow-lg 
                 backdrop-blur-xl border border-primary/20
                 animate-slide-in-top max-w-md w-auto"
    >
      <div className="flex items-center justify-between px-4 py-3">
        {/* Metrics */}
        <button
          onClick={handleGoToRun}
          className="flex items-center gap-3 flex-1 min-w-0"
        >
          <div className="flex flex-col items-start min-w-0">
            <div className="text-sm font-bold">
              {formatTime(localDuration)}
            </div>
            <div className="text-xs opacity-90 truncate">
              {formatDistance(distance)}
            </div>
          </div>
        </button>

        {/* Controls */}
        <div className="flex items-center gap-2 ml-2">
          {status === 'running' ? (
            <Button
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0 rounded-full hover:bg-primary-foreground/20"
              onClick={handlePause}
            >
              <Pause className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0 rounded-full hover:bg-primary-foreground/20"
              onClick={handleResume}
            >
              <Play className="h-4 w-4" />
            </Button>
          )}
          
          <Button
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0 rounded-full hover:bg-destructive/20"
            onClick={handleFinishRun}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Square className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
